import { gql, OperationVariables, TypedDocumentNode } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCustomQuery } from './apollo/ApolloCustomHooks';
import { EnumsValues, ErrorsStatusCode } from '../enums/EnumsValues';
import { IAppSetting } from '../interfaces/AppSetting';
import { ITermsAndConditions } from '../interfaces/TermsAndConditions';
import { IResponseMessage } from '../interfaces/ResponseMessage';
import { Query } from '../services/graphql/query';
import { getContextForRequest } from '../shared/ApolloHooksUtils';
import { getRequestError } from '../shared/requestError';

type ReportError = (options: { context: string; error: unknown }) => void;

export function useAppTerms(
  userId: number | undefined,
  readSession: () => unknown,
  reportError: ReportError,
) {
  const client = useApolloClient();
  const latest = useRef({ readSession, reportError });
  latest.current = { readSession, reportError };
  const [required, setRequired] = useState(false);
  const [document, setDocument] = useState('');
  const [acceptance, setAcceptance] = useState<{
    userId?: number;
    session: unknown;
    accepted: boolean;
  }>();
  const acceptanceRequest = useRef(0);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      acceptanceRequest.current++;
    };
  }, []);
  useEffect(() => {
    if (userId === undefined) {
      acceptanceRequest.current++;
      setAcceptance(undefined);
    }
  }, [userId]);
  const setting = useCustomQuery<{ getAppSettingByKey: IAppSetting }>(
    Query.getAppSettingByKey,
    {
      variables: {
        input: {
          key: EnumsValues.SettingNames.AcceptTermsAndconditionsIsRequired,
        },
      },
    },
  );
  const terms = useCustomQuery<{ termsAndConditions: ITermsAndConditions }>(
    Query.termsAndConditions,
  );

  useEffect(() => {
    if (setting.data)
      setRequired(setting.data.getAppSettingByKey?.setting_value === 't');
  }, [setting.data]);
  useEffect(() => {
    if (setting.error)
      latest.current.reportError({
        context: 'App.termsSetting',
        error: setting.error,
      });
  }, [setting.error]);
  useEffect(() => {
    if (terms.data) setDocument(terms.data.termsAndConditions?.data ?? '');
  }, [terms.data]);
  useEffect(() => {
    if (!terms.error) return;
    if (
      getRequestError(terms.error).status_code ===
      ErrorsStatusCode.recordNotFoundCode
    ) {
      setDocument('');
      return;
    }
    latest.current.reportError({
      context: 'ManageTermsAndConditions.getTermsAndConditionsVersions.1',
      error: terms.error,
    });
  }, [terms.error]);

  const getTermsAndConditions = useCallback(async () => {
    // Refetch errors are presented by the query effect; consume its promise too.
    try {
      await terms.refetch();
    } catch {
      /* Reported above. */
    }
  }, [terms.refetch]);

  const getUserHasAcceptedTermsAndConditions = useCallback(
    async (authenticatedUserId: number) => {
      const session = latest.current.readSession();
      const requestId = ++acceptanceRequest.current;
      const isCurrent = () =>
        active.current &&
        requestId === acceptanceRequest.current &&
        session === latest.current.readSession();
      try {
        // This result belongs to an account/session, although the query has no variables.
        const query = gql(
          Query.userHasAcceptedTermsAndConditions.gql,
        ) as TypedDocumentNode<
          { userHasAcceptedTermsAndConditions: IResponseMessage },
          OperationVariables
        >;
        const { data, error } = await client.query({
          query,
          fetchPolicy: 'no-cache',
          errorPolicy: 'none',
          context: {
            ...getContextForRequest(Query.userHasAcceptedTermsAndConditions),
            queryDeduplication: false,
          },
        });
        if (!isCurrent()) return;
        if (error) throw error;
        setAcceptance({
          userId: authenticatedUserId,
          session,
          accepted: data?.userHasAcceptedTermsAndConditions?.status === true,
        });
      } catch (error) {
        if (!isCurrent()) return;
        setAcceptance({
          userId: authenticatedUserId,
          session,
          accepted: false,
        });
        latest.current.reportError({
          context: 'App.userHasAcceptedTermsAndConditions',
          error,
        });
      }
    },
    [client],
  );

  return {
    termsConfigurationLoading: setting.loading && !setting.data,
    isRequiredAcceptTemrsAndConditions: required,
    setIsRequiredAcceptTemrsAndConditions: setRequired,
    termsAndConditionsVersionActive: document,
    setTermsAndConditionsVersionActive: setDocument,
    userHasAcceptedTermsAndConditions:
      !!userId &&
      acceptance?.userId === userId &&
      acceptance.session === readSession() &&
      acceptance.accepted,
    setUserHasAcceptedTermsAndConditions: (accepted: boolean) =>
      setAcceptance({ userId, session: readSession(), accepted }),
    getTermsAndConditions,
    getUserHasAcceptedTermsAndConditions,
  };
}
